import { FormEvent, useCallback, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { toastModule } from "@/modules/toast";
import { validationModule } from "@/modules/validation";
import { api } from "@/modules/api";
import {
  MemCommonEditor,
  MemCommonEditorActionKind,
  MemCommonEditorEventHandler,
  MemCommonEditorEventKind,
  MemCommonEditorInitializer,
  MemCommonEditorInstance,
  MemCommonEditorTheme,
  editorContextModule,
} from "@mem-labs/common-editor";

const Container = styled.div`
  padding: 32px;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #4a5568;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  border: 1px solid #cbd5f5;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
`;

const EditorWrapper = styled.div`
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 12px;
  min-height: 300px;
  background: white;

  * {
    font-family:
      -apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #718096;
`;

export const NotesCreatePage = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);
  const latestEncodedContentRef = useRef<string | null>(null);

  const editorInitializer: MemCommonEditorInitializer = useCallback(async () => {
    const defaultEditorContext = editorContextModule.generateDefaultEditorContext();

    return {
      encodedContent: "",
      readOnly: false,
      autoFocus: true,
      noteId: "new-note-draft",
      enableWebSockets: false,
      maxUpdateThrottleMillis: 2000,
      theme: MemCommonEditorTheme.Light,
      context: defaultEditorContext,
    };
  }, []);

  const editorEventHandler: MemCommonEditorEventHandler = useCallback(event => {
    if (event.kind === MemCommonEditorEventKind.EditorContentRendered) {
      setIsEditorReady(true);
    }
    if (event.kind === MemCommonEditorEventKind.DocumentChanged) {
      latestEncodedContentRef.current = event.payload.encodedContentDiff;
      setHasContent(!event.payload.isEmpty);
    }
    if (
      [MemCommonEditorEventKind.ContentError, MemCommonEditorEventKind.FatalError].includes(
        event.kind
      )
    ) {
      toastModule.triggerToast({ content: `Editor error: ${event.kind}` });
    }
  }, []);

  const getEncodedContent = (): string | null => {
    // First try to flush any pending updates
    if (editorInstanceRef.current) {
      const result = editorInstanceRef.current.dispatchAction({
        kind: MemCommonEditorActionKind.FlushUpdates,
        payload: null,
      });
      if (result?.flushedUpdates?.encodedContentDiff) {
        latestEncodedContentRef.current = result.flushedUpdates.encodedContentDiff;
      }
    }
    return latestEncodedContentRef.current;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedSpaceAccountId = spaceAccountId.trim();

    if (!validationModule.isUuid(trimmedSpaceAccountId)) {
      toastModule.triggerToast({ content: "Space account ID must be a valid UUID." });
      return;
    }

    const encodedContent = getEncodedContent();
    if (!encodedContent) {
      toastModule.triggerToast({ content: "Please enter some content for the note." });
      return;
    }

    setIsSubmitting(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (api.post as any)("/internal/v1/notes/create", {
        body: {
          space_account_id: trimmedSpaceAccountId,
          encoded_content: encodedContent,
        },
      });

      if (!response.data?.note_id) {
        throw new Error("Missing note_id in response");
      }

      toastModule.triggerToast({ content: "Note created successfully." });
      navigate(atlasRoutes.notesView({ params: { noteId: response.data.note_id } }).path);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create note.";
      toastModule.triggerToast({ content: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <div>
        <Title>Create Note</Title>
        <Subtitle>Create a new note for a specific user using the rich text editor.</Subtitle>
      </div>

      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="space-account-id">Target Space Account ID</Label>
          <Input
            id="space-account-id"
            value={spaceAccountId}
            onChange={event => setSpaceAccountId(event.target.value)}
            placeholder="Enter the user's space account UUID"
            required
          />
          <HelpText>The note will be created and owned by this space account.</HelpText>
        </Field>

        <Field>
          <Label>Note Content</Label>
          <EditorWrapper>
            <MemCommonEditor
              editorInstanceRef={editorInstanceRef}
              editorInitializer={editorInitializer}
              editorEventHandler={editorEventHandler}
            />
          </EditorWrapper>
          <HelpText>Use the rich text editor to compose your note content.</HelpText>
        </Field>

        <ButtonRow>
          <Button type="submit" disabled={isSubmitting || !isEditorReady || !hasContent}>
            {isSubmitting ? "Creating..." : "Create Note"}
          </Button>
          <Button type="button" variant="soft" onClick={() => navigate(atlasRoutes.notes({}).path)}>
            Cancel
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
};
